import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import {
    urlImportDocNumberConvention,
    urlParamReplicaId,
    urlParamUniversalId,
    urlProjectDocumentDataSave,
} from '../../Constants';
import { IAction, SlicePropsWithData } from '../store';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IDocnumberSidepanelState = {
    data: null,
    loading: false,
    error: null,
    isSidepanelOpen: false,
    isIndexFieldDisabled: false,
    loadingSaveDocNumber: false,
    errorSaveDocNumber: null,
    selectedNode: '',
    selectedNodes: [],
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    toggleDocnumberSidepanel(
        state: IDocnumberSidepanelState,
        action: IAction<boolean | { open: boolean; disableIndexField?: boolean }>,
    ) {
        if (typeof action.payload === 'boolean') {
            state.isSidepanelOpen = action.payload;
            state.isIndexFieldDisabled = false;
        } else {
            state.isSidepanelOpen = action.payload.open;
            state.isIndexFieldDisabled = Boolean(action.payload.disableIndexField);
        }
    },
    setSelectedNodes(state: IDocnumberSidepanelState, action: IAction<ISelectedNodes>) {
        state.selectedNode = action.payload.current;
        state.selectedNodes = action.payload.all;
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Fetches a data for docNumber.
     * @param config - Object that contains the project database's replicaId, universalId
     * @returns Promise for the HTTP request.
     */
    getDocNumberConvention: async (config: {
        replicaId: string;
        universalId: string;
    }): Promise<IDocnumberSidepanelData> => {
        try {
            const { universalId, replicaId } = config;

            const url = urlImportDocNumberConvention
                .replace(urlParamReplicaId, replicaId)
                .replace(urlParamUniversalId, universalId);

            const response = await apiFetch(url);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Saves new docNumber.
     */
    saveDocNumber: async (config: {
        replicaId: string;
        universalId: string;
        updatedDocNumer: IResultDocNumberObjValue;
    }): Promise<void> => {
        try {
            const { replicaId, universalId, updatedDocNumer } = config;

            const url = urlProjectDocumentDataSave
                .replace(urlParamReplicaId, replicaId)
                .replace(urlParamUniversalId, universalId);

            /** Note: Backend only accepts data in form-format! */
            const formData = new FormData();
            formData.append('documentData', JSON.stringify(updatedDocNumer));

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve()
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IDocnumberSidepanelState>) => {
        /**
         * Reducers for the 'getDocNumberConvention' thunk.
         */
        builder.addCase(
            getDocNumberConvention.pending,
            (state: Draft<IDocnumberSidepanelState>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            getDocNumberConvention.fulfilled,
            (state: Draft<IDocnumberSidepanelState>, action: IAction<IDocnumberSidepanelData>) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            getDocNumberConvention.rejected,
            (state: Draft<IDocnumberSidepanelState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
                state.data = null;
            },
        );

        /**
         * Reducers for the 'saveDocNumber' thunk.
         */
        builder.addCase(saveDocNumber.pending, (state: Draft<IDocnumberSidepanelState>) => {
            state.loadingSaveDocNumber = true;
            state.errorSaveDocNumber = null;
        });
        builder.addCase(saveDocNumber.fulfilled, (state: Draft<IDocnumberSidepanelState>) => {
            state.loadingSaveDocNumber = false;
            state.errorSaveDocNumber = null;
        });
        builder.addCase(
            saveDocNumber.rejected,
            (state: Draft<IDocnumberSidepanelState>, action: IAction<SerializedError>) => {
                state.loadingSaveDocNumber = false;
                state.errorSaveDocNumber = action.error;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getDocNumberConvention = createAsyncThunk(
    'import/docnumberconvention',
    thunks.getDocNumberConvention,
);
export const saveDocNumber = createAsyncThunk('import/saveDocNumber', thunks.saveDocNumber);

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IDocnumberSidepanelState extends SlicePropsWithData<IDocnumberSidepanelData> {
    /** Defines if sidepanel for docNumber is open */
    isSidepanelOpen: boolean;

    /** Wether the DocIndex field is disabled. */
    isIndexFieldDisabled: boolean;

    /** Defines if DocNumber is saving. */
    loadingSaveDocNumber: boolean;

    /** Error that is thrown during resource fetching. */
    errorSaveDocNumber: SerializedError;

    /** Identifier of the current selected node. */
    selectedNode: string;

    /** Array with identifiers of selected nodes. */
    selectedNodes: string[];
}

/**
 * Interface that describes the DocTypeConvention's section value.
 */
export interface IDocTypeConventionsSectionValue {
    key: string;
    displayValue: string;
}

/**
 * Interface that describes the DocTypeConvention's section.
 */
export interface IDocTypeConventionsSection {
    displayName: string;
    optional: boolean;
    seperator: string;
    value: string;
    displayType: 'textinput' | 'dropdown';
    minLength?: number;
    maxLength?: number;
    values?: IDocTypeConventionsSectionValue[];
    prohibitedCharacters?: string;
    fieldName?: string;
}

/*
 * Interface that describes the DocTypeConvention.
 */
export interface IDocTypeConvention {
    example: string;
    key: string;
    selected: boolean;
    sections: IDocTypeConventionsSection[];
}

/**
 * Interface that describes the data for sidepanel.
 */
export interface IDocnumberSidepanelData {
    DocTypeConventions: IDocTypeConvention[];
    docNumber: string;
    docType: string;
    filename: string;
    subject: string;
    universialId: string;
}

/**
 * Interface that describes the object for save data.
 */
export interface IResultDocNumberObjValue {
    DocNumber: string;
    DocIndex: string;
    Subject?: string;
}

interface ISelectedNodes {
    current: string;
    all: string[];
}

/**
 * Create the redux slice.
 */
export const docnumberSidepanelSlice = createSlice({
    name: 'docnumberSidepanel',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const { toggleDocnumberSidepanel, setSelectedNodes } = docnumberSidepanelSlice.actions;

/**
 * Export slice reducer as default.
 */
export default docnumberSidepanelSlice.reducer;
